/**
 * Client-side tenant/permission session (Phase 8).
 *
 * A tiny module-level singleton that mirrors the load-bearing bits of the
 * authenticated session — the JWT, the active tenant, and the effective
 * permission set — so that the scattered fetch utilities (which don't have
 * access to React state) can attach the `X-Tenant-Id` header centrally, and
 * so the tenant survives component remounts.
 *
 * IMPORTANT: this is a UX convenience only. The server remains the security
 * boundary (strategy §5, P8 DoD) — every permission is re-checked server-side.
 * The active tenant id is mirrored to localStorage purely so the global fetch
 * interceptor keeps sending the right header across remounts; the JWT is kept
 * in memory only, matching the app's existing in-memory auth posture.
 */

const TENANT_KEY = 'activeTenantId';

const state = {
  token: null,
  tenantId: null,
  permissions: [],
  roles: [],
  memberships: [],
  tenantContext: null,
};

// Rehydrate only the (non-secret) active tenant id so the header interceptor
// works even before React has re-run the login/bootstrap flow.
try {
  const persisted = localStorage.getItem(TENANT_KEY);
  if (persisted) state.tenantId = persisted;
} catch (e) { /* localStorage unavailable — degrade to memory only */ }

export const setSessionToken = (token) => { state.token = token || null; };
export const getSessionToken = () => state.token;

export const setActiveTenantId = (tenantId) => {
  state.tenantId = tenantId != null ? String(tenantId) : null;
  try {
    if (state.tenantId) localStorage.setItem(TENANT_KEY, state.tenantId);
    else localStorage.removeItem(TENANT_KEY);
  } catch (e) { /* ignore */ }
};
export const getActiveTenantId = () => state.tenantId;

export const setPermissions = (permissions) => {
  state.permissions = Array.isArray(permissions) ? permissions : [];
};
export const getPermissions = () => state.permissions;

export const setRoles = (roles) => { state.roles = Array.isArray(roles) ? roles : []; };
export const getRoles = () => state.roles;

export const setMemberships = (memberships) => {
  state.memberships = Array.isArray(memberships) ? memberships : [];
};
export const getMemberships = () => state.memberships;

export const setTenantContext = (ctx) => { state.tenantContext = ctx || null; };
export const getTenantContext = () => state.tenantContext;

/** Replace the whole session in one shot after login / tenant switch. */
export const applySession = ({ token, tenantId, permissions, roles, memberships, tenantContext }) => {
  if (token !== undefined) setSessionToken(token);
  if (tenantId !== undefined) setActiveTenantId(tenantId);
  if (permissions !== undefined) setPermissions(permissions);
  if (roles !== undefined) setRoles(roles);
  if (memberships !== undefined) setMemberships(memberships);
  if (tenantContext !== undefined) setTenantContext(tenantContext);
};

export const clearSession = () => {
  state.token = null;
  state.tenantId = null;
  state.permissions = [];
  state.roles = [];
  state.memberships = [];
  state.tenantContext = null;
  try { localStorage.removeItem(TENANT_KEY); } catch (e) { /* ignore */ }
};

/** True if the current session carries the named permission. */
export const sessionHasPermission = (permission) =>
  Array.isArray(state.permissions) && state.permissions.indexOf(permission) !== -1;
